import React, { useEffect, useState } from "react";
import api, { getErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { Award, BookOpenCheck, ArrowRight, Sparkles } from "lucide-react";

const LEVELS = ["A1", "A2", "B1", "B2", "C1", "C2"];

const LEVEL_TONE = {
  A1: "bg-red-50 text-red-700 border-red-200",
  A2: "bg-orange-50 text-orange-700 border-orange-200",
  B1: "bg-amber-50 text-amber-700 border-amber-200",
  B2: "bg-lime-50 text-lime-700 border-lime-200",
  C1: "bg-emerald-50 text-emerald-700 border-emerald-200",
  C2: "bg-teal-50 text-teal-800 border-teal-200",
};

const LEVEL_TAGLINE_EN = {
  A1: "Beginner — basic phrases & familiar daily expressions.",
  A2: "Elementary — routine tasks & simple direct exchanges.",
  B1: "Intermediate — handles most travel & familiar topics.",
  B2: "Upper-intermediate — fluent on abstract & technical topics.",
  C1: "Advanced — flexible, effective use in complex contexts.",
  C2: "Mastery — near-native precision and nuance.",
};
const LEVEL_TAGLINE_ZH = {
  A1: "入门 — 能掌握基本短语与日常表达。",
  A2: "初级 — 能处理日常简单交流。",
  B1: "中级 — 能在大多数熟悉话题中沟通。",
  B2: "中高级 — 能就抽象与专业话题流畅表达。",
  C1: "高级 — 在复杂语境中灵活运用。",
  C2: "精通 — 接近母语者的精准与细腻。",
};

export default function CefrCard() {
  const { lang } = useLang();
  const [phase, setPhase] = useState("idle"); // idle | running | finished
  const [testCode, setTestCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [attempt, setAttempt] = useState(null);
  const [question, setQuestion] = useState(null);
  const [progress, setProgress] = useState({ answered: 0, total: 20, current_level: "B1" });
  const [selectedOption, setSelectedOption] = useState(null);
  const [lastResult, setLastResult] = useState(null);
  const [pastAttempts, setPastAttempts] = useState([]);

  async function loadPast() {
    try {
      const { data } = await api.get("/cefr/me/attempts");
      setPastAttempts(data || []);
    } catch { /* ignore */ }
  }
  useEffect(() => { (async () => { await loadPast(); })(); }, []);

  const startTest = async () => {
    if (!testCode.trim()) { toast.error("Enter your test code"); return; }
    setBusy(true);
    try {
      const resp = await api.post("/cefr/start", { code: testCode.trim().toUpperCase() });
      const data = resp.data;
      setAttempt(data.attempt);
      setQuestion(data.next_question);
      setProgress(data.progress || { answered: 0, total: 20, current_level: "B1" });
      setSelectedOption(null);
      setPhase("running");
    } catch (err) { toast.error(getErrorDetail(err)); }
    finally { setBusy(false); }
  };

  async function submitAnswer() {
    if (selectedOption === null) { toast.error("Pick an option first"); return; }
    if (!question || !attempt) return;
    setBusy(true);
    try {
      const { data } = await api.post("/cefr/answer", { attempt_id: attempt.id, question_id: question.id, answer_index: selectedOption });
      if (data.result) {
        setLastResult({ final_level: data.result.final_level, level_stats: data.result.level_stats });
        setAttempt(data.attempt);
        setQuestion(null);
        setPhase("finished");
        // Refresh history list asynchronously (don't block).
        api.get("/cefr/me/attempts").then((r) => setPastAttempts(r.data || [])).catch(() => {});
      } else {
        setAttempt(data.attempt);
        setQuestion(data.next_question);
        setProgress(data.progress || progress);
        setSelectedOption(null);
      }
    } catch (err) { toast.error(getErrorDetail(err)); }
    finally { setBusy(false); }
  }

  function resetToIdle() {
    setPhase("idle"); setTestCode(""); setAttempt(null); setQuestion(null); setLastResult(null); setSelectedOption(null);
  }

  // ----- Render variants ---------------------------------------------------

  if (phase === "running" && question) {
    const pct = Math.min(100, Math.round((progress.answered / (progress.total || 20)) * 100));
    return (
      <section data-testid="cefr-test-running" className="rounded-2xl border border-[hsl(var(--border))] bg-white p-6 md:p-8 mt-12">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-[hsl(var(--muted-foreground))]">{lang === "zh" ? "CEFR 分级测试 · 进行中" : "CEFR Placement Test · in progress"}</p>
            <h2 className="font-display text-2xl tracking-tight mt-2">{lang === "zh" ? "选择最合适的答案" : "Pick the best answer"}</h2>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">{lang === "zh" ? "进度" : "Progress"}</div>
            <div className="font-display text-xl tracking-tight mt-1" data-testid="cefr-progress-count">{progress.answered + 1} / {progress.total || 20}</div>
            <div className="mt-2 h-1.5 w-40 bg-[hsl(var(--muted))] rounded-full overflow-hidden">
              <div className="h-full bg-[hsl(var(--primary))] transition-all" style={{ width: `${pct}%` }} />
            </div>
          </div>
        </div>

        <div className="mt-7 space-y-5">
          {question.passage && (
            <div data-testid="cefr-passage" className="rounded-xl bg-[hsl(var(--muted))]/40 border border-[hsl(var(--border))] p-4 text-sm leading-relaxed text-[hsl(var(--foreground))]/80 italic">
              {question.passage}
            </div>
          )}
          <div data-testid="cefr-prompt" className="font-display text-xl tracking-tight font-medium leading-snug">{question.prompt}</div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {question.options.map((opt, i) => (
              <button
                key={i}
                type="button"
                onClick={()=>setSelectedOption(i)}
                data-testid={`cefr-option-${i}`}
                className={`text-left rounded-xl border p-4 transition-all ${selectedOption === i ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5 ring-2 ring-[hsl(var(--primary))]/30' : 'border-[hsl(var(--border))] hover:border-[hsl(var(--foreground))]/30 bg-white'}`}
              >
                <div className="flex items-start gap-3">
                  <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium border ${selectedOption === i ? 'bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]' : 'border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))]'}`}>{String.fromCharCode(65+i)}</span>
                  <span className="flex-1 text-sm leading-relaxed pt-0.5">{opt}</span>
                </div>
              </button>
            ))}
          </div>

          <Button onClick={submitAnswer} disabled={busy || selectedOption === null} data-testid="cefr-submit-answer" className="rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white px-6 mt-2">
            {busy ? "…" : (lang === "zh" ? "提交答案" : "Submit answer")}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <p className="text-xs text-[hsl(var(--muted-foreground))]">{lang === "zh" ? "答错也没关系 — 系统会自动调整难度。请尽你所能。" : "Don't worry about wrong answers — difficulty adjusts automatically. Do your best."}</p>
        </div>
      </section>
    );
  }

  if (phase === "finished" && lastResult) {
    const tagline = (lang === "zh" ? LEVEL_TAGLINE_ZH : LEVEL_TAGLINE_EN)[lastResult.final_level];
    return (
      <section data-testid="cefr-test-result" className="rounded-2xl border border-[hsl(var(--border))] bg-white p-6 md:p-8 mt-12 text-center">
        <Sparkles className="w-6 h-6 mx-auto text-[hsl(var(--primary))]" />
        <p className="text-xs uppercase tracking-[0.25em] text-[hsl(var(--muted-foreground))] mt-3">{lang === "zh" ? "测试完成" : "Test complete"}</p>
        <h2 className="font-display text-3xl md:text-4xl tracking-tight mt-2">{lang === "zh" ? "你的 CEFR 水平是" : "Your CEFR level is"}</h2>
        <div className={`inline-block mt-5 px-8 py-3 rounded-full text-3xl font-display font-medium tracking-tight border ${LEVEL_TONE[lastResult.final_level]}`} data-testid="cefr-final-level">
          {lastResult.final_level}
        </div>
        <p className="mt-4 max-w-md mx-auto text-[hsl(var(--muted-foreground))]">{tagline}</p>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-2 text-xs">
          {LEVELS.map(lvl => {
            const s = lastResult.level_stats?.[lvl];
            if (!s || s.total === 0) return null;
            return (
              <span key={lvl} data-testid={`cefr-stat-${lvl}`} className="px-2.5 py-1 rounded-full bg-[hsl(var(--muted))]">{lvl}: {s.correct}/{s.total}</span>
            );
          })}
        </div>

        <Button onClick={resetToIdle} variant="outline" data-testid="cefr-result-back" className="rounded-full mt-8">{lang === "zh" ? "返回门户" : "Back to my portal"}</Button>
      </section>
    );
  }

  // idle: code-entry card + past attempts
  return (
    <section data-testid="cefr-test-card" className="mt-12">
      <div className="flex items-center gap-2 mb-5">
        <BookOpenCheck className="w-4 h-4 text-[hsl(var(--primary))]" />
        <h2 className="font-display text-2xl tracking-tight">{lang === "zh" ? "CEFR 分级测试" : "CEFR Placement Test"}</h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7 rounded-2xl border border-[hsl(var(--border))] bg-white p-6" data-testid="cefr-code-form">
          <p className="text-sm text-[hsl(var(--muted-foreground))]">{lang === "zh" ? "请输入老师发给您的 8 位测试码。测试约需 10–15 分钟，20 道自适应题目（A1 – C2）。如果您之前已经开始，可重新输入同一个码继续。" : "Enter the 8-character test code your teacher sent you. The test takes 10–15 minutes — 20 adaptive questions (A1 to C2). If you started earlier, re-enter the same code to resume where you left off."}</p>
          <div className="mt-4">
            <Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">{lang === "zh" ? "测试码" : "Test code"}</Label>
            <Input
              value={testCode}
              onChange={(e)=>setTestCode(e.target.value.toUpperCase())}
              maxLength={12}
              placeholder="e.g. PQMEUFED"
              data-testid="cefr-code-input"
              className="mt-2 font-mono tracking-[0.25em] text-lg uppercase"
            />
          </div>
          <Button onClick={startTest} disabled={busy} data-testid="cefr-code-submit" className="rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white mt-4">
            {busy ? "…" : (lang === "zh" ? "开始测试" : "Start test")}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>

        <aside className="lg:col-span-5 rounded-2xl border border-dashed border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 p-6" data-testid="cefr-past-attempts">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[hsl(var(--muted-foreground))]"><Award className="w-3.5 h-3.5" />{lang === "zh" ? "我的历史成绩" : "My past results"}</div>
          <ul className="mt-3 space-y-2">
            {pastAttempts.filter(a => a.status === "completed").length === 0 && (
              <li className="text-sm text-[hsl(var(--muted-foreground))] italic">{lang === "zh" ? "还没有完成的测试。" : "No completed tests yet."}</li>
            )}
            {pastAttempts.filter(a => a.status === "completed").map((a) => (
              <li key={a.id} data-testid={`cefr-past-${a.id}`} className="flex items-center justify-between gap-3 text-sm">
                <span className="text-[hsl(var(--muted-foreground))]">{format(parseISO(a.finished_at), "MMM dd, yyyy")}</span>
                <span className={`px-2 py-0.5 rounded-full border text-xs font-medium ${LEVEL_TONE[a.final_level]}`}>{a.final_level}</span>
              </li>
            ))}
          </ul>
        </aside>
      </div>
    </section>
  );
}
